//@flow
import type { AppState, ID } from '../../core/types'
import type {
  Checkout,
  CheckoutItem,
  HomeDeliveryMode,
  ShippingOption,
  DeliveryType
} from './types'
import type { Item } from '../item/types'
import { createSelector, Selector } from 'reselect'
import { smembers } from '../../modules/set/index'
import {
  USER_CHECKOUTS_SET,
  USER_CHECKOUT_ITEMS_SET,
  FETCH_PENDING_CHECKOUTS_RESOURCE,
  FETCH_USER_CHECKOUTS_RESOURCE,
  CHECKOUT_STATE
} from './constants'
import {
  getDomain,
  getById,
  type EntityMap
} from '../../modules/entities/index'
import { domains } from '../constants'
import { isCommCompleted } from '../../modules/communication'
import { hget } from '../../modules/hash'
import { getAddresses, type UserAddress } from '../session'

export const getAllCheckouts: Selector<Checkout[]> = createSelector(
  smembers(USER_CHECKOUTS_SET),
  getDomain(domains.CHECKOUT),
  function(allIds: string[], checkoutsById: EntityMap<Checkout>) {
    return (allIds || []).map(id => checkoutsById[id])
  }
)

export const areUserCheckoutsLoaded: Selector<boolean> = state =>
  isCommCompleted(FETCH_USER_CHECKOUTS_RESOURCE, state)

export const arePendingCheckoutsLoaded: Selector<boolean> = state =>
  isCommCompleted(FETCH_PENDING_CHECKOUTS_RESOURCE, state)

export const getPendingCheckouts: Selector<Checkout[]> = createSelector(
  getAllCheckouts,
  function(allCheckouts: Checkout[]) {
    return allCheckouts.filter(
      checkout => checkout.state !== CHECKOUT_STATE.COMPLETED
    )
  }
)

export const getPendingCheckoutIds: Selector<string[]> = createSelector(
  getPendingCheckouts,
  function(checkouts: Checkout[]) {
    return checkouts.map(checkout => checkout.id)
  }
)

export const getPendingCheckoutError: Selector<boolean> = createSelector(
  getPendingCheckouts,
  function(checkouts: Checkout[]) {
    return checkouts.some(
      (x: Checkout) => x.state === CHECKOUT_STATE.PAYMENT_FAIL
    )
  }
)

export const getCheckoutById = (id: string): Selector<Checkout> =>
  createSelector(
    getById(domains.CHECKOUT, id),
    getDomain(domains.AUCTIONS),
    (checkout, allAuctions) => {
      return (
        checkout && {
          ...checkout,
          auction: checkout.auctionId && allAuctions[checkout.auctionId]
        }
      )
    }
  )

export const getCheckoutItemById = (id: ID): Selector<CheckoutItem> =>
  getById(domains.CHECKOUT_ITEM, id)

export const getCheckoutItems = (
  id: string,
  state: AppState
): CheckoutItem[] => {
  const checkout = getCheckoutById(id)(state)
  const checkoutItems = getDomain(domains.CHECKOUT_ITEM, state)
  return (checkout.checkoutItems || []).map(itemId => checkoutItems[itemId])
}

export const makeGetSummaryCheckoutItems = (
  checkoutId: string
): Selector<CheckoutItem[]> =>
  createSelector(
    getCheckoutById(checkoutId),
    getDomain(domains.CHECKOUT_ITEM),
    getDomain(domains.ITEMS),
    (
      checkout: Checkout,
      allCheckoutItems: EntityMap<CheckoutItem>,
      allItems: EntityMap<Item>
    ) => {
      const checkoutItems =
        checkout && checkout.checkoutItems
          ? checkout.checkoutItems.map(id => allCheckoutItems[id.toString()])
          : []
      return checkoutItems.map(checkoutItem => {
        const item = allItems[checkoutItem.itemId.toString()]
        return {
          id: checkoutItem.id,
          itemId: checkoutItem.itemId,
          title: item.title,
          amount: checkoutItem.amount
        }
      })
    }
  )

// for the shopping cart icon in the header
export const getPendingCheckoutCount: Selector<number> = createSelector(
  getPendingCheckouts,
  function(pendingCheckouts: Checkout[]) {
    return pendingCheckouts.filter(x =>
      [CHECKOUT_STATE.PAYMENT_FAIL, CHECKOUT_STATE.PAYMENT_OK].includes(x.state)
    ).length
  }
)

export const getPendingCheckoutForAuctionId = (
  auctionId: number,
  state: AppState
): Selector<?Checkout> => {
  const pendingCheckouts: Checkout[] = getPendingCheckouts(state)
  return pendingCheckouts.find(checkout => checkout.auctionId === auctionId)
}

export const getFinishedCheckouts: Selector<Checkout[]> = createSelector(
  getAllCheckouts,
  function(allCheckouts: Checkout[]) {
    return allCheckouts.filter(
      checkout =>
        checkout.state === CHECKOUT_STATE.COMPLETED ||
        checkout.state === CHECKOUT_STATE.EXPIRED
    )
  }
)

export const getAllCheckoutItems: Selector<CheckoutItem[]> = createSelector(
  smembers(USER_CHECKOUT_ITEMS_SET),
  getDomain(domains.CHECKOUT_ITEM),
  function(allIds: string[], allCheckoutItems: EntityMap<CheckoutItem>) {
    return (allIds || []).map(id => allCheckoutItems[id])
  }
)

export const getDeliveryType: Selector<DeliveryType> = hget(
  domains.CHECKOUT,
  'deliveryType'
)

export const getShippingOption: Selector<ShippingOption> = hget(
  domains.CHECKOUT,
  'shippingOption'
)

export const getHomeDeliveryMode: Selector<HomeDeliveryMode> = hget(
  domains.CHECKOUT,
  'homeDeliveryMode'
)
export const getUserAddressId: Selector<string> = hget(
  domains.CHECKOUT,
  'userAddressId'
)

export const getSelectedAddress: Selector<UserAddress> = createSelector(
  getUserAddressId,
  getAddresses,
  (addressId, allUserAddresses) => {
    if (!addressId) {
      return allUserAddresses.length ? allUserAddresses[0] : null
    }
    return allUserAddresses.find(address => address.id === addressId)
  }
)

export const getAreCheckoutActionButtonsVisible: Selector<
  boolean
> = createSelector(getHomeDeliveryMode, homeDeliveryMode =>
  Boolean(homeDeliveryMode !== 'create')
)



// WEBPACK FOOTER //
// ./src/services/checkout/selectors.js